/**
 * The app's one connection to the staff stream, and the hooks that read it.
 *
 * The shell opens it while somebody is signed in (`startLive`); a screen
 * hears events with `useLiveEvents`, asks for points with `useLivePositions`,
 * and reads with `useLiveQuery`, which asks again by itself only as a
 * backstop: often while the stream is down, seldom while it is up.
 */
import {
  useQuery,
  type DefaultError,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useRef, useSyncExternalStore } from "react";

import { realtimeApi, unwrap } from "@/shared/api/clients";
import { isProblem } from "@/shared/api/problem";
import { serviceUrl } from "@/shared/api/services";

import { POSITION_EVENT_TYPE, type LiveEvent } from "./events";
import { LiveStream, type LiveStatus } from "./stream";

/** How often a screen asks by itself while the stream is not connected. */
export const POLL_WHEN_DOWN_MS = 15_000;
/**
 * And while it is: a backstop, not the way the screen moves. Some changes
 * have no event (who took a visit of the queue), and a browser cannot tell a
 * stream that has silently died from one with nothing to say.
 */
export const POLL_WHEN_LIVE_MS = 60_000;

export function streamUrl({
  ticket,
  lastEventId,
  positions,
}: {
  ticket: string;
  lastEventId: string | null;
  positions: boolean;
}): string {
  const query = new URLSearchParams({ ticket });
  if (lastEventId !== null) query.set("last_event_id", lastEventId);
  if (positions) query.set("positions", "true");
  return `${serviceUrl("realtime")}/v2/realtime/staff/events?${query.toString()}`;
}

async function askTicket(): Promise<string> {
  const { data } = await unwrap(realtimeApi.POST("/v2/realtime/tickets"));
  return data.ticket;
}

export const liveStream = new LiveStream({
  ticket: askTicket,
  open: (url) => new EventSource(url),
  url: streamUrl,
  problemCode: (error) => (isProblem(error) ? error.code : null),
});

const nudge = () => {
  if (document.visibilityState !== "hidden") liveStream.nudge();
};

/** Opens the stream. In a browser without `EventSource` nothing is opened: the screens ask by themselves. */
export function startLive(): void {
  if (typeof EventSource === "undefined") return;
  liveStream.start();
  // The network is back, or the tab is looked at again: do not wait out the pause.
  window.addEventListener("online", nudge);
  document.addEventListener("visibilitychange", nudge);
}

export function stopLive(): void {
  window.removeEventListener("online", nudge);
  document.removeEventListener("visibilitychange", nudge);
  liveStream.stop();
}

const subscribeStatus = (onChange: () => void) => liveStream.onStatus(onChange);

export function useLiveStatus(): LiveStatus {
  return useSyncExternalStore(subscribeStatus, () => liveStream.status());
}

/** How often a query that the stream keeps current still asks by itself. */
export function useLivePoll(): number {
  return useLiveStatus().state === "connected" ? POLL_WHEN_LIVE_MS : POLL_WHEN_DOWN_MS;
}

/**
 * `useQuery` for what the stream keeps current: events say when to read it
 * again (the shell's bridge), and it asks by itself only as often as
 * `useLivePoll` says.
 */
export function useLiveQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): UseQueryResult<TData, TError> {
  const refetchInterval = useLivePoll();
  return useQuery({ ...options, refetchInterval });
}

/** Hears every event of the stream while the component is on the screen. */
export function useLiveEvents(handler: (event: LiveEvent) => void): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  useEffect(
    () =>
      liveStream.onEvent((event) => {
        latest.current(event);
      }),
    [],
  );
}

let positionListeners = 0;

/**
 * Hears the points of the specialists at work while the component is on the
 * screen. The stream carries them only while somebody listens: the first
 * listener opens it anew with them, the last one's leaving without.
 */
export function useLivePositions(handler: (event: LiveEvent) => void): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  useEffect(() => {
    positionListeners += 1;
    liveStream.setPositions(true);
    const stop = liveStream.onEvent((event) => {
      if (event.type === POSITION_EVENT_TYPE) latest.current(event);
    });
    return () => {
      stop();
      positionListeners -= 1;
      if (positionListeners === 0) liveStream.setPositions(false);
    };
  }, []);
}
